import React, { useCallback, useEffect, useState } from "react";
import { Check, ChevronDown, Globe, Loader2, RotateCw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../ui/command";
import {
  LANGUAGES, applyLanguage, getCurrentLanguage, loadGoogleTranslate,
} from "../../lib/googleTranslate";

const labelFor = (code) => LANGUAGES.find((l) => l.code === code)?.label || "English";

const StatusNote = ({ status, error, onRetry }) => {
  if (status === "unavailable") {
    return (
      <div className="notranslate flex items-center justify-between gap-2 px-3 py-2 text-xs text-amber-700 bg-amber-50" data-testid="language-unavailable">
        <span>Translation is temporarily unavailable.</span>
        <button type="button" onClick={onRetry} className="inline-flex items-center gap-1 font-semibold underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cleanora-primary rounded" data-testid="language-retry-btn">
          <RotateCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }
  if (error) return <p className="notranslate px-3 py-2 text-xs text-red-600 bg-red-50" data-testid="language-error">{error}</p>;
  return null;
};

const Attribution = () => (
  <p className="px-3 py-2 text-[11px] text-slate-500 border-t border-slate-100 notranslate" data-testid="google-translate-attribution">
    Automatic translations powered by{" "}
    <a href="https://translate.google.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-600 hover:text-cleanora-primary">Google Translate</a>. English is the original text.
  </p>
);

const TriggerLabel = ({ status, pending, active }) => {
  if (pending) return <><Loader2 className="w-4 h-4 animate-spin" /><span>Translating…</span></>;
  if (status === "loading") return <><Loader2 className="w-4 h-4 animate-spin" /><span>Languages</span></>;
  return <><Globe className="w-4 h-4 text-cleanora-primary" /><span className="notranslate">{labelFor(active)}</span></>;
};

const useTranslator = () => {
  const [status, setStatus] = useState("loading");
  const [active, setActive] = useState(getCurrentLanguage);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setStatus("loading");
    loadGoogleTranslate().then(() => setStatus("ready")).catch(() => setStatus("unavailable"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const choose = async (code) => {
    if (code === active || pending) return;
    setError("");
    setPending(code);
    const ok = await applyLanguage(code).catch(() => false);
    setPending(null);
    if (ok) setActive(code);
    else setError(`Couldn't switch to ${labelFor(code)}. Please try again.`);
  };

  return { status, active, pending, error, choose, load };
};

export const LanguageSelector = ({ className = "" }) => {
  const [open, setOpen] = useState(false);
  const { status, active, pending, error, choose, load } = useTranslator();

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label={`Choose language, current: ${labelFor(active)}`}
            aria-busy={Boolean(pending) || status === "loading"}
            className={`notranslate inline-flex items-center gap-2 h-11 px-3 rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:border-cleanora-primary/50 hover:bg-cleanora-soft-mist transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cleanora-primary focus-visible:ring-offset-2 ${className}`}
            data-testid="language-selector-trigger"
          >
            <TriggerLabel status={status} pending={pending} active={active} />
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 motion-reduce:transition-none ${open ? "rotate-180" : ""}`} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" collisionPadding={16} className="w-[min(18rem,calc(100vw-2rem))] p-0 overflow-hidden rounded-xl border-slate-200 shadow-xl" data-testid="language-selector-panel">
          <Command>
            <CommandInput placeholder="Search languages…" aria-label="Search languages" data-testid="language-search-input" />
            <StatusNote status={status} error={error} onRetry={load} />
            <CommandList className="max-h-64">
              <CommandEmpty data-testid="language-empty">No language found.</CommandEmpty>
              <CommandGroup>
                {LANGUAGES.map((lang) => (
                  <CommandItem
                    key={lang.code}
                    value={`${lang.label} ${lang.english} ${lang.code}`}
                    disabled={Boolean(pending) || (status !== "ready" && !(lang.code === "en" && active !== "en"))}
                    onSelect={() => { choose(lang.code); if (lang.code !== "en") setOpen(false); }}
                    className="notranslate flex items-center justify-between gap-3 py-2.5 cursor-pointer"
                    data-testid={`language-option-${lang.code}`}
                  >
                    <span className="flex flex-col">
                      <span dir="auto" className="font-medium text-slate-800">{lang.label}</span>
                      {lang.label !== lang.english && <span className="text-xs text-slate-500">{lang.english}</span>}
                    </span>
                    {pending === lang.code ? <Loader2 className="w-4 h-4 animate-spin text-cleanora-primary" />
                      : <Check className={`w-4 h-4 text-cleanora-primary ${active === lang.code ? "opacity-100" : "opacity-0"}`} />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            <Attribution />
          </Command>
        </PopoverContent>
      </Popover>
      <span className="sr-only" aria-live="polite">{pending ? `Translating to ${labelFor(pending)}` : ""}</span>
    </>
  );
};

export default LanguageSelector;
